/**
 * The shared route composition: identity, explanation, status, one primary act,
 * secondary acts, then content. It fetches nothing and grants nothing; hosts
 * supply already-governed words, actions and children.
 *
 * This is deliberately a recipe rather than another atom. The console had
 * independently restated `.page`, `.head` and `.title` across dozens of style
 * sheets. A page with a different business body should still begin with the
 * same calm hierarchy and responsive action grammar.
 */
import { useId, type ReactNode } from 'react'
import s from './PageShell.module.css'

export type PageWidth = 'content' | 'wide' | 'full'

/**
 * THE PAGE TYPE (#3014): the ONE width rule. Pages had improvised it — Pipeline,
 * Pursuits and Catalog full width, Engagements, Interactions and the Timesheet
 * narrow and centred — because `width` was a free choice per route. A type names
 * what the page IS and the width follows from it:
 *
 *   list     → `full`  — a collection reads across the whole frame
 *   overview → `wide`  — a dashboard, settings or explanation keeps a readable max
 *   record   → `full`  — a record page's own master + related columns need the room
 *
 * Declaring a type also opts the page into the kit's COLUMN and SPACING model:
 * the content is one stack whose sections are spaced by `--gap-section`, with
 * an optional `aside` column. A page with no type and no aside renders exactly
 * as before. An explicit `width` still wins, as the escape hatch a page states
 * a reason for in a comment.
 */
export type PageType = 'list' | 'overview' | 'record'

const PAGE_TYPE_WIDTH: Readonly<Record<PageType, PageWidth>> = {
  list: 'full',
  overview: 'wide',
  record: 'full',
}

export interface PageShellProps {
  title: ReactNode
  /** Routes default to one h1; embedded examples can preserve their heading order. */
  headingLevel?: 1 | 2 | 3
  eyebrow?: ReactNode
  summary?: ReactNode
  status?: ReactNode
  /** Secondary actions render before the single, visually final primary act. */
  secondaryActions?: ReactNode
  primaryAction?: ReactNode
  /** A restrained, high-emphasis identity band for a true landing surface. */
  appearance?: 'default' | 'feature'
  /** Compact governed facts that belong to that identity band. */
  highlights?: ReactNode
  /** What the page IS; sets the width and opts into the column/spacing model. */
  type?: PageType
  /**
   * The secondary column (#3014): beside the main stack when the page is at
   * least 880px wide, stacked below it when narrower. DOM order is visual order —
   * the aside always follows the main content.
   */
  aside?: ReactNode
  /** Overrides the type's width. Prefer `type`. */
  width?: PageWidth
  testId?: string
  children: ReactNode
}

export function PageShell({
  title,
  headingLevel = 1,
  eyebrow,
  summary,
  status,
  secondaryActions,
  primaryAction,
  appearance = 'default',
  highlights,
  type,
  aside,
  width,
  testId,
  children,
}: PageShellProps) {
  const resolvedWidth: PageWidth = width ?? (type != null ? PAGE_TYPE_WIDTH[type] : 'wide')
  /* The layout model is opt-in so the 70-odd routes that predate it stay
     byte-identical until each one declares its type. */
  const layout = aside != null ? 'aside' : type != null ? 'single' : undefined
  const generatedTitleId = useId()
  const titleId = `page-title-${generatedTitleId.replace(/:/g, '')}`
  const hasActions = secondaryActions != null || primaryAction != null
  const Heading = `h${headingLevel}` as 'h1' | 'h2' | 'h3'

  return (
    <section className={s.page} data-width={resolvedWidth} data-page-type={type} data-testid={testId}
      aria-labelledby={titleId}>
      <header className={s.header} data-appearance={appearance}>
        <div className={s.identity}>
          {eyebrow != null && <div className={s.eyebrow}>{eyebrow}</div>}
          <div className={s.titleLine}>
            <Heading className={s.title} id={titleId}>{title}</Heading>
            {status != null && <div className={s.status}>{status}</div>}
          </div>
          {summary != null && <div className={s.summary}>{summary}</div>}
        </div>

        {hasActions && (
          <div className={s.actions} data-page-actions>
            {secondaryActions}
            {primaryAction}
          </div>
        )}
        {highlights != null && <div className={s.highlights} data-page-highlights>{highlights}</div>}
      </header>

      {layout == null
        ? <div className={s.content}>{children}</div>
        : (
          <div className={s.content} data-page-layout={layout}>
            <div className={s.stack} data-page-main="" data-page-stack="">{children}</div>
            {aside != null && <div className={s.stack} data-page-aside="" data-page-stack="">{aside}</div>}
          </div>
        )}
    </section>
  )
}
